import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Plus, Search, Trash2, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { describePromoRates, formatPercent, newRateGroupUid, type DraftRateGroup } from '@/lib/promoRates'
import type { Category, Product, PromoDiscountType, PromoScope } from '@/types'

export interface PromoScopeValue {
  scope: PromoScope
  categoryIds: string[]
  productIds: string[]
  /** F-MKT-N4: extra rates (percent codes only). */
  rateGroups: DraftRateGroup[]
  otherOn: boolean
  otherPercent: string
}

interface PromoScopePickerProps extends PromoScopeValue {
  discountType: PromoDiscountType
  /** The code's main rate (the Value field), for the summary. */
  mainValue: string
  disabled?: boolean
  onChange: (next: PromoScopeValue) => void
}

/**
 * F-MKT-N2 — "Applies to" for a promo code: the whole bill, or only some
 * menu categories and/or individual items. The discount and the minimum
 * spend are then worked out on the matching items only (server-side).
 *
 * F-MKT-N4 — a % code can also give other categories their own rate and put
 * a rate on everything else: "20% Espresso Based & Tea · 15% everything else".
 * Still one code and one discount on the bill.
 */
export function PromoScopePicker(props: PromoScopePickerProps) {
  const { scope, categoryIds, productIds, rateGroups, otherOn, otherPercent, discountType, mainValue, disabled, onChange } = props
  const itemsMode = scope === 'items'
  const percent = discountType === 'percent'

  const categoriesQuery = useQuery({
    queryKey: ['promo-scope', 'categories'],
    queryFn: () => apiClient.getCategories(false),
    enabled: itemsMode,
    staleTime: 5 * 60_000,
  })
  const productsQuery = useQuery({
    queryKey: ['promo-scope', 'products'],
    queryFn: () => apiClient.getAllProducts(),
    enabled: itemsMode,
    staleTime: 5 * 60_000,
  })

  const categories = useMemo(
    () => [...(categoriesQuery.data?.data ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)),
    [categoriesQuery.data]
  )
  const categoryName = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])

  const value: PromoScopeValue = { scope, categoryIds, productIds, rateGroups, otherOn, otherPercent }
  const set = (patch: Partial<PromoScopeValue>) => onChange({ ...value, ...patch })

  // Which rate each target already has, so a target sits in one group only.
  // Index -1 is the main rate.
  const owner = useMemo(() => {
    const cats = new Map<string, number>()
    const prods = new Map<string, number>()
    categoryIds.forEach((id) => cats.set(id, -1))
    productIds.forEach((id) => prods.set(id, -1))
    rateGroups.forEach((g, i) => {
      g.categoryIds.forEach((id) => cats.set(id, i))
      g.productIds.forEach((id) => prods.set(id, i))
    })
    return { cats, prods }
  }, [categoryIds, productIds, rateGroups])

  const rateLabel = (idx: number) => {
    const raw = idx === -1 ? mainValue : rateGroups[idx]?.percent
    const n = Number(raw)
    return raw && Number.isFinite(n) && n > 0 ? formatPercent(n) : idx === -1 ? 'the main rate' : 'another rate'
  }

  const multi = percent && (rateGroups.length > 0 || otherOn)
  const summary = useMemo(() => {
    if (!multi) return ''
    const names = (cats: string[], prods: string[]) =>
      [...cats.map((id) => categoryName.get(id) ?? ''), ...prods.map((id) => productById.get(id)?.name ?? '')].filter(Boolean)
    return describePromoRates(
      { percent: Number(mainValue) || 0, names: names(categoryIds, productIds) },
      rateGroups.map((g) => ({ percent: Number(g.percent) || 0, names: names(g.categoryIds, g.productIds) })),
      otherOn ? Number(otherPercent) || null : null,
    )
  }, [multi, mainValue, categoryIds, productIds, rateGroups, otherOn, otherPercent, categoryName, productById])

  const updateGroup = (i: number, patch: Partial<DraftRateGroup>) =>
    set({ rateGroups: rateGroups.map((g, j) => (j === i ? { ...g, ...patch } : g)) })

  return (
    <div className="space-y-3 rounded-lg border border-border/70 p-3">
      <div className="space-y-1.5">
        <Label>Applies to</Label>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5" role="radiogroup">
          {(
            [
              ['bill', 'Whole bill'],
              ['items', 'Some categories or items'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={scope === v}
              disabled={disabled}
              onClick={() => set({ scope: v })}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm transition-colors disabled:opacity-50',
                scope === v ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {itemsMode && (
        <>
          <p className="text-sm text-muted-foreground">
            The discount and minimum spend count only the matching items. Items that already have a manual discount are skipped.
          </p>

          {multi && <p className="text-sm font-medium">{rateLabel(-1)} off on</p>}
          <TargetPicker
            idPrefix="promo-scope"
            categories={categories}
            categoriesLoading={categoriesQuery.isLoading}
            products={products}
            productsLoading={productsQuery.isLoading}
            productById={productById}
            categoryName={categoryName}
            categoryIds={categoryIds}
            productIds={productIds}
            takenBy={(kind, id) => {
              const o = (kind === 'c' ? owner.cats : owner.prods).get(id)
              return o !== undefined && o !== -1 ? rateLabel(o) : null
            }}
            disabled={disabled}
            onChange={(next) => set(next)}
          />
          {categoryIds.length + productIds.length === 0 && (
            <p className="text-sm text-amber-700 dark:text-amber-400">Pick at least one category or item.</p>
          )}

          {percent ? (
            <div className="space-y-3 border-t border-border/70 pt-3">
              {rateGroups.map((g, i) => (
                <div key={g.uid ?? i} className="space-y-2 rounded-lg border border-border/70 p-3">
                  <div className="flex items-center gap-2">
                    <Input
                      aria-label={`Rate ${i + 2} percent`}
                      className="w-24"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step="0.01"
                      value={g.percent}
                      onChange={(e) => updateGroup(i, { percent: e.target.value })}
                      placeholder="e.g. 10"
                      disabled={disabled}
                    />
                    <span className="text-sm">% off on</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-muted-foreground"
                      disabled={disabled}
                      onClick={() => set({ rateGroups: rateGroups.filter((_, j) => j !== i) })}
                      aria-label={`Remove rate ${i + 2}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <TargetPicker
                    idPrefix={`promo-rate-${g.uid ?? i}`}
                    categories={categories}
                    categoriesLoading={categoriesQuery.isLoading}
                    products={products}
                    productsLoading={productsQuery.isLoading}
                    productById={productById}
                    categoryName={categoryName}
                    categoryIds={g.categoryIds}
                    productIds={g.productIds}
                    takenBy={(kind, id) => {
                      const o = (kind === 'c' ? owner.cats : owner.prods).get(id)
                      return o !== undefined && o !== i ? rateLabel(o) : null
                    }}
                    disabled={disabled}
                    onChange={(next) => updateGroup(i, next)}
                  />
                </div>
              ))}
              {rateGroups.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => set({ rateGroups: [...rateGroups, { uid: newRateGroupUid(), percent: '', categoryIds: [], productIds: [] }] })}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Different rate for other categories
                </Button>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Switch id="promo-other-on" checked={otherOn} disabled={disabled} onCheckedChange={(on) => set({ otherOn: on })} />
                <Label htmlFor="promo-other-on" className="font-normal">
                  Rate on everything else
                </Label>
                {otherOn && (
                  <>
                    <Input
                      aria-label="Percent on everything else"
                      className="w-24"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step="0.01"
                      value={otherPercent}
                      onChange={(e) => set({ otherPercent: e.target.value })}
                      placeholder="e.g. 15"
                      disabled={disabled}
                    />
                    <span className="text-sm text-muted-foreground">% off</span>
                  </>
                )}
              </div>

              {summary && (
                <p className="rounded-lg bg-muted/50 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">This code gives </span>
                  {summary}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Different rates by category work with a percent code.</p>
          )}
        </>
      )}
    </div>
  )
}

interface TargetPickerProps {
  idPrefix: string
  categories: Category[]
  categoriesLoading: boolean
  products: Product[]
  productsLoading: boolean
  productById: Map<string, Product>
  categoryName: Map<string, string>
  categoryIds: string[]
  productIds: string[]
  /** The rate label of another group that already has this target, else null. */
  takenBy: (kind: 'c' | 'p', id: string) => string | null
  disabled?: boolean
  onChange: (next: { categoryIds: string[]; productIds: string[] }) => void
}

function TargetPicker({
  idPrefix,
  categories,
  categoriesLoading,
  products,
  productsLoading,
  productById,
  categoryName,
  categoryIds,
  productIds,
  takenBy,
  disabled,
  onChange,
}: TargetPickerProps) {
  const [q, setQ] = useState('')
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return products.filter((p) => p.name.toLowerCase().includes(needle)).slice(0, 30)
  }, [products, q])

  const toggleCategory = (id: string) =>
    onChange({ categoryIds: categoryIds.includes(id) ? categoryIds.filter((x) => x !== id) : [...categoryIds, id], productIds })
  const toggleProduct = (id: string) =>
    onChange({ categoryIds, productIds: productIds.includes(id) ? productIds.filter((x) => x !== id) : [...productIds, id] })

  return (
    <>
      <div className="space-y-1.5">
        <Label>Categories</Label>
        {categoriesLoading ? (
          <p className="text-sm text-muted-foreground">Loading categories…</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">No categories yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => {
              const on = categoryIds.includes(c.id)
              const taken = on ? null : takenBy('c', c.id)
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={disabled || !!taken}
                  aria-pressed={on}
                  title={taken ? `Already at ${taken}` : undefined}
                  onClick={() => toggleCategory(c.id)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-sm transition-colors disabled:opacity-50',
                    on ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  {on && <Check className="h-3.5 w-3.5 text-primary" />}
                  {c.name}
                  {!c.is_active && <span className="text-xs text-muted-foreground">(hidden)</span>}
                  {taken && <span className="text-xs text-muted-foreground">· {taken}</span>}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-search`}>Individual items</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id={`${idPrefix}-search`}
            className="pl-9"
            placeholder={productsLoading ? 'Loading the menu…' : 'Search the menu to add an item'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
        {q.trim() !== '' && (
          <div className="max-h-48 overflow-y-auto rounded-lg border border-border/70">
            {results.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">No items match.</p>
            ) : (
              results.map((p) => {
                const on = productIds.includes(p.id)
                const taken = on ? null : takenBy('p', p.id)
                const viaCategory = !on && !!p.category_id && categoryIds.includes(p.category_id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={disabled || !!taken}
                    onClick={() => toggleProduct(p.id)}
                    className={cn(
                      'flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted/50 disabled:opacity-50',
                      on && 'bg-primary/5'
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {taken
                        ? `already at ${taken}`
                        : viaCategory
                          ? `already in ${categoryName.get(p.category_id!)}`
                          : p.category_id
                            ? categoryName.get(p.category_id)
                            : ''}
                    </span>
                    {on && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                )
              })
            )}
          </div>
        )}
        {productIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {productIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 rounded-lg border border-primary bg-primary/10 py-1 pl-2.5 pr-1 text-sm">
                {productById.get(id)?.name ?? (productsLoading ? '…' : 'Removed item')}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleProduct(id)}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label="Remove item"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
